import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { passwordChangeActions, passwordChangeSelectors } from '../reducer/password-change-reducer';
import styled from '@emotion/styled';
import { lionSelectors } from '@rosetta/react-lion';
import { LaunchpadButtonNew, Link } from '../../../styles/LaunchpadButton';
import { appActions, appSelectors, TRANSACTION_STATE, ACCOUNT_TYPE } from '../../app/reducer/app-reducer';
import { getSupportLocale } from '../../app/services/language-helpers';
import { MouseCatcherContainer as MouseCatcher } from '../../app/util/MouseCatcher';
import { validateUserInfo } from '../../app/util/user-helper';
import { MultipleAccountsContainer } from '../../app/components/MultipleAccountsContainer';
import { LoginContainer } from './LoginContainer';
import { TextInput } from '../../app/util/TextInput';
import { breakpoints } from '../../../styles/constants';

const WORKSPACE_REQUIRED_ERROR = 'error_workspace_required_to_use_username';

const P = styled.p`
  padding-bottom: 6px;
  margin-top: -5px;
  text-align: left;
  ${breakpoints.SCREEN_XS} {
    text-align: center;
  }
`;

const WorkspaceHelpLink = styled(Link)`
  font-weight: 400;
  margin-top: -14px;
  font-size: 12px;
  align-self: baseline;
`;

const Form = styled.form`
  margin: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: -12px;
  /* inconsistent with the other elements, spacing is now 20px, not 12px */
  & > *:not(:last-child) {
    margin-bottom: 20px;
  }
`;

const isValidUsername = (username) => {
  const MIN_USERNAME_LENGTH = 1;
  const MAX_USERNAME_LENGTH = 63;
  if (!username) {
    return false;
  }
  // Note, usernames may contains '@' and '.' among other chars.
  const length = username.length;
  return length >= MIN_USERNAME_LENGTH && length <= MAX_USERNAME_LENGTH;
};

export class ForgotPassword extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      emailAddress: this.props.getUserEmail,
      isWorkspaceRequired: false,
      tempWorkspace: '',
      selectedFlow: null // null, 'classic', or 'narwhal'
    };
  }

  componentDidMount() {
    // Clear any error messages
    this.props.errorKeyUpdated(null);
  }

  handleEmailChange = (event) => {
    // they are changing the email so set the transaction to a new one, if they had one
    this.props.setLastTransactionState(TRANSACTION_STATE.NO_TRANSACTION);
    this.setState({ emailAddress: event.target.value.trim() });
  };

  handleWorkspaceChange = (event) => {
    // this.props.clearAuthError();
    this.props.setLastTransactionState(TRANSACTION_STATE.NO_TRANSACTION);
    this.setState({ tempWorkspace: event.target.value.trim() });
  };

  handleSignInHelpClick = (event) => {
    const locale = getSupportLocale(this.props.currentLocale);
    window.open(
      'https://support.rosettastone.com/s/article/I-cannot-sign-into-Rosetta-Stone-Language-Learning-Online-What-can-I-do?language=' +
        locale,
      '_blank'
    );
  };

  handleWorkspaceHelpClick = (event) => {
    const locale = getSupportLocale(this.props.currentLocale);
    window.open(
      'https://support.rosettastone.com/s/article/What-is-my-Namespace-for-the-Rosetta-Stone-Mobile-App?language=' +
        locale,
      '_blank'
    );
  };

  handleFlowSelection = (flow) => {
    this.setState({ selectedFlow: flow });
  };

  submitForgotPasswordRequest = (e) => {
    // We use try catch because in case of CORS error, page (ie, cancel) no longer works.
    e.preventDefault();

    if (this.state.selectedFlow === 'narwhal') {
      if (!isValidUsername(this.state.emailAddress)) {
        return;
      }

      try {
        this.props.requestNarwhalPasswordReset(this.state.emailAddress, this.props.currentLocale || 'en-US');
      } catch (e) {
        console.log(e);
      }
      return;
    }

    // Classic flow
    if (!this.isSubmitDisabled()) {
      // check if input is in a valid email format.
      // if yes, do requestPasswordReset(); else, assume workspace is required.
      if (!this.emailError()) {
        try {
          this.props.requestPasswordReset(
            this.state.emailAddress,
            this.state.tempWorkspace,
            '',
            this.props.currentLocale
          );
        } catch (e) {
          console.log(e);
        }
      } else {
        this.setState({ isWorkspaceRequired: true });
        this.props.errorKeyUpdated('notice_specify_workspace_to_reset_password');
        if (this.state.tempWorkspace) {
          try {
            this.props.requestPasswordReset(
              this.state.emailAddress,
              this.state.tempWorkspace,
              '',
              this.props.currentLocale
            );
          } catch (e) {
            console.log(e);
          }
        }
      }
    }
  };

  returnToLogin = () => {
    this.props.logoutUser();
  };

  emailError = () => {
    return validateUserInfo('email', this.state.emailAddress);
  };

  isSubmitDisabled = () => {
    // return this.props.lastTransactionState !== TRANSACTION_STATE.NO_TRANSACTION || this.emailError().length > 0;
    return (
      this.props.lastTransactionState !== TRANSACTION_STATE.NO_TRANSACTION || !isValidUsername(this.state.emailAddress)
    );
  };

  handleAccountOptionClick = (accountType) => {
    if (accountType === ACCOUNT_TYPE.PERSONAL) {
      this.props.requestPasswordReset(this.state.emailAddress, '', ACCOUNT_TYPE.PERSONAL, this.props.currentLocale);
    } else if (accountType === ACCOUNT_TYPE.BUSINESS) {
      this.props.setLastTransactionState(TRANSACTION_STATE.NO_TRANSACTION);
      this.setState({ isWorkspaceRequired: true });
      this.props.errorKeyUpdated('error_workspace_required_to_use_username');
    } else {
    }
  };

  render = () => {
    if (!this.props.lionLoaded || this.props.lastTransactionState === TRANSACTION_STATE.PENDING) {
      return <MouseCatcher />;
    }

    // Show flow selection if user hasn't chosen yet
    if (this.state.selectedFlow === null) {
      return (
        <LoginContainer header={this.props.getLionTranslatedString('welcome')}>
          <P>Which account type would you like to reset your password for?</P>
          <Form>
            <MultipleAccountsContainer>
              <Link data-qa="NarwhalFlowButton" onClick={() => this.handleFlowSelection('narwhal')}>
                Sapphire account (2026)
              </Link>
              <Link data-qa="ClassicFlowButton" onClick={() => this.handleFlowSelection('classic')}>
                Any other account (personal, enterprise, or educational)
              </Link>
            </MultipleAccountsContainer>
            <Link data-qa="FlowSelectionCancelLink" onClick={() => this.returnToLogin()}>
              {this.props.getLionTranslatedString('forgotpassword_cancel_link')}
            </Link>
          </Form>
        </LoginContainer>
      );
    }
    let weNeedWorkspace = false;
    if (this.props.errorKey && this.props.errorKey === WORKSPACE_REQUIRED_ERROR) {
      //this.setState({ isWorkspaceRequired: true });
      weNeedWorkspace = true;
    }

    if (
      this.props.isBothPersonalAndBusiness !== null &&
      this.props.lastTransactionState === TRANSACTION_STATE.ACCOUNT_CHOOSE
    ) {
      if (this.props.isBothPersonalAndBusiness) {
        return (
          <LoginContainer header={this.props.getLionTranslatedString('welcome')}>
            <Form>
              <P>
                {this.props.getLionTranslatedString('multiple_accounts_found')}
                <br />
                {this.props.getLionTranslatedString('which_account_to_reset_password')}
              </P>
              <MultipleAccountsContainer>
                <Link
                  data-qa={'PersonalAccountOption'}
                  onClick={() => this.handleAccountOptionClick(ACCOUNT_TYPE.PERSONAL)}
                >
                  {this.props.getLionTranslatedString('personal')}
                </Link>
                <Link
                  data-qa={'BusinessAccountOption'}
                  onClick={() => this.handleAccountOptionClick(ACCOUNT_TYPE.BUSINESS)}
                >
                  {this.props.getLionTranslatedString('school_slash_business')}
                </Link>
              </MultipleAccountsContainer>
              <Link data-qa="PopUpCancelLink" onClick={() => this.returnToLogin()}>
                {this.props.getLionTranslatedString('forgotpassword_cancel_link')}
              </Link>
            </Form>
          </LoginContainer>
        );
      } else {
        this.setState({ isWorkspaceRequired: true });
      }
    }

    // If server returns `not allowed`, hide everything except `return home` button, and show a message
    // Or, on success, let them know an email was sent.
    if (
      this.props.lastTransactionState === TRANSACTION_STATE.SUCCESS ||
      this.props.errorKey === 'org_not_allowed_password_change'
    ) {
      let text =
        this.state.isWorkspaceRequired || weNeedWorkspace
          ? this.props.getLionTranslatedString('reset_password_username_instructions_sent')
          : this.props.getLionTranslatedString('reset_password_email_instructions_sent');
      if (this.props.errorKey === 'org_not_allowed_password_change') {
        text = this.props.getLionTranslatedString('password_reset_not_allowed_by_org');
      }
      return (
        <LoginContainer>
          <P>{text}</P>
          <LaunchpadButtonNew data-qa="returnHomeButton" onClick={this.returnToLogin}>
            {/* {this.props.getLionTranslatedString('forgotpassword_state2_button_ok')} */}
            {this.props.getLionTranslatedString('return_home')}
          </LaunchpadButtonNew>
        </LoginContainer>
      );
    }

    // Server may return `REQUEST_INVALID` but in Lion we have it as REQUEST_NEW_CODE
    // if (errorKey === 'REQUEST_INVALID') {
    //   errorKey = 'REQUEST_NEW_CODE';
    // }

    let WorkspaceBlock = null;
    if (this.state.isWorkspaceRequired || weNeedWorkspace) {
      WorkspaceBlock = (
        <React.Fragment>
          <TextInput
            inputType="text"
            dataQa="Workspace"
            placeholder={this.props.getLionTranslatedString('login_workspace_field')}
            tabIndex={2}
            value={this.state.tempWorkspace}
            onChange={this.handleWorkspaceChange}
          />
          <WorkspaceHelpLink data-qa="WorkspaceHelpLink" onClick={this.handleWorkspaceHelpClick}>
            {this.props.getLionTranslatedString('what_is_my_workspace')}
          </WorkspaceHelpLink>
        </React.Fragment>
      );
    }
    let errorKey = this.props.errorKey;
    if (
      errorKey &&
      (errorKey === 'error_workspace_required_to_use_username' ||
        errorKey === 'error_workspace_required_to_login_with_username' ||
        errorKey === 'a_workspace_is_required')
    ) {
      errorKey = 'notice_specify_workspace_to_reset_password';
    }
    return (
      <LoginContainer
        header={this.props.getLionTranslatedString('login_forgot_password_link')}
        errorMessage={errorKey ? this.props.getLionTranslatedString(errorKey) : null}
      >
        <P>
          {this.state.isWorkspaceRequired || weNeedWorkspace
            ? this.props.getLionTranslatedString('prompt_enter_email_workspace')
            : this.props.getLionTranslatedString('prompt_enter_email')}
        </P>
        <Form>
          <TextInput
            type="text"
            dataQa="Email"
            value={this.state.emailAddress}
            autoFocus={true}
            tabIndex={1}
            onChange={this.handleEmailChange}
            onBlur={this.handleEmailBlur}
            placeholder={
              this.state.isWorkspaceRequired || weNeedWorkspace
                ? this.props.getLionTranslatedString('placeholder_username_email')
                : this.props.getLionTranslatedString('login_email_field')
            }
          />
          {WorkspaceBlock}
          <LaunchpadButtonNew
            data-qa="ResetPasswordButton"
            onClick={this.submitForgotPasswordRequest}
            disabled={this.isSubmitDisabled()}
            tabIndex={3}
          >
            {this.props.getLionTranslatedString('_submit')}
          </LaunchpadButtonNew>

          <Link data-qa="ForgotPasswordCancelLink" onClick={() => this.returnToLogin()}>
            {this.props.getLionTranslatedString('forgotpassword_cancel_link')}
          </Link>
        </Form>
      </LoginContainer>
    );
  };
}

const mapStateToProps = (state) => ({
  lionLoaded: lionSelectors.getLionAppResourcesLoaded(state),
  currentLocale: lionSelectors.getLionLanguage(state),
  getLionTranslatedString: (key) => lionSelectors.getLionTranslatedString(state, key),
  getUserEmail: passwordChangeSelectors.getUserEmail(state),
  lastTransactionError: appSelectors.lastTransactionError(state),
  lastTransactionState: appSelectors.lastTransactionState(state),
  isBothPersonalAndBusiness: appSelectors.isBothPersonalAndBusiness(state),
  errorKey: appSelectors.errorKey(state)
});

const wrapActions = {
  setLastTransactionState: appActions.setLastTransactionState,
  MobileSsoLoginDiv: appActions.MobileSsoLoginDivMobileSsoLoginDiv,
  logoutUser: appActions.logoutUser,
  requestPasswordReset: passwordChangeActions.requestPasswordReset,
  requestNarwhalPasswordReset: passwordChangeActions.requestNarwhalPasswordReset,
  errorKeyUpdated: appActions.errorKeyUpdated
};

export const ForgotPasswordContainer = connect(
  mapStateToProps,
  wrapActions
)(ForgotPassword);



// WEBPACK FOOTER //
// ./src/modules/login/components/ForgotPassword.jsx